import { useCallback, useMemo, useState } from "react";
import styles from "./index.module.scss";
import { StyledCheckboxOrRadio } from "components/inputs/checkbox/StyledCheckboxOrRadio";
import classnames from "classnames/bind";
import { Button } from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "hooks/useIsMobile";
const cx = classnames.bind(styles);
import { GenericMultiSelectProps } from "components/generic-multiselect/types";
import { ItemsList } from "./ItemsList";
import { Spinner } from "components/spinners/Spinner";
import { BroadcastResponsesSearchSortBar } from "components/inputs/search-sort-bar/BroadcastResponsesSearchSortBar";
import {
    AssetDetailsResponse,
    BroadcastResponse
} from "@switcherstudio/api-core-client";
import { useOnScroll } from "hooks/useOnScroll";
import { AssetsSearchSortBar } from "components/inputs/search-sort-bar/AssetsSearchSortBar";

export function GenericMultiSelect<T, Y>({
    items,
    handleSelect,
    handleSelectAll = () => {},
    showNone = false,
    allowUnselect = true,
    isMultiple = false,
    allowItemClick = true,
    checkBoxLocation = "right",
    searchSortOptions = { showSearchSort: false },
    actionsBarOptions = { showActions: false },
    loading = false,
    selectedIdSet,
    previouslySelectedIds = [],
    displayOrder = items.map((i) => i.id),
    setDisplayOrder,
    visibleItems = items,
    emptyStateCopy,
    showSelectedCountOnActions,
    getNextPage,
    nextPageLoading,
    scrollable = false
}: GenericMultiSelectProps<T, Y>) {
    const { t } = useTranslation();

    const [hasBeenSorted, setHasBeenSorted] = useState(false);

    useOnScroll({
        onScroll: getNextPage,
        disable: loading,
        loading: nextPageLoading
    });

    const handleSelectLocal = useCallback(
        (id: string) => {
            if (!isMultiple && selectedIdSet.has(id)) return;
            handleSelect && handleSelect(id);
        },
        [handleSelect, isMultiple, selectedIdSet]
    );

    const { isMobile } = useIsMobile();

    const handleAction = useCallback(
        async (
            onClick: (
                listItemsProps: Y[],
                handleSelectAll: (displayOrder?: string[]) => void
            ) => Promise<any>,
            shouldDeselectOnClick: boolean | undefined
        ) => {
            let listItemsPropsArray: Y[] = [...selectedIdSet].map((id) => {
                const item = items.find((item) => item.id === id);
                return item?.component.props as Y;
            });

            await onClick(listItemsPropsArray, handleSelectAll);
            // Clears selection
            if (shouldDeselectOnClick || shouldDeselectOnClick === undefined) {
                handleSelectAll();
            }
        },
        [handleSelectAll, items, selectedIdSet]
    );

    const searchSortComponent = useMemo(() => {
        if (!searchSortOptions?.showSearchSort) return <></>;

        if (searchSortOptions?.location === "cloud-assets") {
            return (
                <AssetsSearchSortBar
                    assets={items.map(
                        (item) => item.baseObject as AssetDetailsResponse
                    )}
                    handleSort={(assets: AssetDetailsResponse[]) => {
                        setDisplayOrder(
                            assets.map((a) => {
                                return a.Id;
                            })
                        );
                        setHasBeenSorted(true);
                    }}
                    showOrientation={searchSortOptions?.showOrientation}
                />
            );
        }

        return (
            <BroadcastResponsesSearchSortBar
                searchSortOptions={searchSortOptions}
                broadcasts={items.map(
                    (item) => item.baseObject as BroadcastResponse
                )}
                handleSort={(broadcasts: BroadcastResponse[]) => {
                    setDisplayOrder(
                        broadcasts.map((b) => {
                            return b.Details.Id;
                        })
                    );
                    setHasBeenSorted(true);
                }}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, setDisplayOrder]);
    const actionsBarComponent = useMemo(() => {
        if (!actionsBarOptions.showActions) return <></>;
        return (
            <div
                className={`${styles["actions-bar"]} select-many-list-display-actions-bar`}
            >
                {isMultiple && (
                    <Button
                        onClick={() => handleSelectAll(displayOrder)}
                        type="text"
                        disabled={!displayOrder.length}
                    >
                        {
                            // Any visible items that are selected
                            selectedIdSet.size > 0 &&
                            displayOrder
                                .filter(
                                    (id) =>
                                        !previouslySelectedIds ||
                                        !previouslySelectedIds.includes(id)
                                )
                                .some((id) => selectedIdSet.has(id))
                                ? t("buttons:deselect-all") +
                                  (scrollable
                                      ? " " + t("buttons:scroll-to-load-more")
                                      : "")
                                : t("buttons:select-all")
                        }
                    </Button>
                )}
                <div className={styles["action-buttons"]}>
                    {actionsBarOptions.actions.map(
                        ({
                            onClick,
                            text,
                            buttonType,
                            shouldDeselectOnClick,
                            MobileIcon
                        }) => {
                            return (
                                isMobile !== undefined && (
                                    <Button
                                        onClick={() =>
                                            handleAction(
                                                onClick,
                                                shouldDeselectOnClick
                                            )
                                        }
                                        type={isMobile ? "icon" : buttonType}
                                        disabled={!selectedIdSet.size}
                                        key={text}
                                    >
                                        {isMobile ? (
                                            <MobileIcon />
                                        ) : (
                                            `${text}${
                                                showSelectedCountOnActions &&
                                                selectedIdSet.size > 0
                                                    ? ` (${selectedIdSet.size})`
                                                    : ""
                                            }`
                                        )}
                                    </Button>
                                )
                            );
                        }
                    )}
                </div>
            </div>
        );
    }, [
        isMultiple,
        actionsBarOptions,
        displayOrder,
        selectedIdSet,
        t,
        handleSelectAll,
        isMobile,
        handleAction,
        showSelectedCountOnActions,
        scrollable,
        previouslySelectedIds
    ]);

    const placeholderComponent = useMemo(() => {
        const placeholderSelector = (
            <StyledCheckboxOrRadio
                type="radio"
                checked={[...selectedIdSet].filter(Boolean).length === 0}
                onChange={() => handleSelectLocal(undefined)}
            />
        );

        return (
            <div
                className={cx("ms-card", {
                    "checkbox-left": checkBoxLocation === "left",
                    "allow-item-click": allowItemClick
                })}
                onClick={() => allowItemClick && handleSelectLocal(undefined)}
            >
                {checkBoxLocation === "left" && placeholderSelector}
                <div className={cx("ms-card-component")}>
                    <h6 style={{ marginBottom: "0" }}>{`None`}</h6>
                </div>
                {checkBoxLocation !== "left" && placeholderSelector}
            </div>
        );
    }, [allowItemClick, checkBoxLocation, handleSelectLocal, selectedIdSet]);

    const showEmptyState = useMemo<boolean>(() => {
        if (searchSortOptions?.showSearchSort) {
            return hasBeenSorted && !visibleItems?.length;
        }

        return !visibleItems?.length;
    }, [hasBeenSorted, searchSortOptions, visibleItems]);

    // Apply all sorting, filtering, and other logic to get the final items to display
    const finalItems = useMemo(() => {
        // If no items, return empty array
        if (!items || !items?.length) return [];

        // If no display order (or if there is no search/sort functionality), return all items
        if (
            displayOrder === undefined ||
            !searchSortOptions?.showSearchSort ||
            (displayOrder.length === 0 &&
                searchSortComponent.props.broadcasts?.length !== 0)
        ) {
            return items;
        }

        // Otherwise, return the items mapped to the displayOrder
        return displayOrder
            ?.filter((id) => !!items.find((i) => i.id === id))
            ?.map((id) => items.find((i) => i.id === id));
    }, [
        items,
        displayOrder,
        searchSortOptions?.showSearchSort,
        searchSortComponent.props.broadcasts
    ]);

    const emptyState = useMemo(() => {
        if (typeof emptyStateCopy === "string") {
            return (
                <p className={styles["empty-state-container"]}>
                    {emptyStateCopy}
                </p>
            );
        } else {
            return emptyStateCopy;
        }
    }, [emptyStateCopy]);

    return (
        <div className={styles["ms-container"]}>
            {searchSortComponent}
            {actionsBarComponent}
            {(loading ||
                (searchSortOptions?.showSearchSort && !hasBeenSorted)) && (
                <div className={styles["loading"]}>
                    <Spinner size={128} />
                </div>
            )}
            {showEmptyState && !loading ? (
                emptyState
            ) : (
                <>
                    {showNone && !isMultiple && placeholderComponent}
                    <ItemsList
                        items={finalItems}
                        allowItemClick={allowItemClick}
                        checkBoxLocation={checkBoxLocation}
                        isMultiple={isMultiple}
                        handleSelectLocal={handleSelectLocal}
                        selectedIdSet={selectedIdSet}
                        previouslySelectedIds={previouslySelectedIds}
                        allowUnselect={allowUnselect}
                    />
                    {nextPageLoading && !loading && <Spinner size={64} />}
                </>
            )}
        </div>
    );
}
